import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  ComputerDesktopIcon, 
  WrenchScrewdriverIcon, 
  CogIcon, 
  AcademicCapIcon,
  TrophyIcon,
  UserIcon,
  GlobeAltIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

interface SkillsProps {
  skills: any; // We'll restructure this completely
}

const Skills: React.FC<SkillsProps> = ({ skills }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Skills data
  const skillsData = {
    itSupport: [
      'Help Desk Operations', 'Desktop Support', 'Technical Service', 
      'Troubleshooting', 'Problem Solving'
    ],
    hardware: [
      'PC Building', 'Laptop Repair & Upgrades', 'Printer Management'
    ],
    operatingSystems: [
      'Windows', 'Windows Server'
    ],
    productivity: [
      'Microsoft Office 365 Applications', 'G Suite'
    ],
    administration: [
      'Active Directory', 'MDM', 'Office 365 Admin', 
      'G Suite Admin', 'Hyper-V'
    ],
    web: [
      'HTML', 'CSS', 'WordPress', 'Wix'
    ],
    personal: [
      'Customer Service', 'Flexibility', 'Teamwork', 'Communication'
    ]
  };

  // Education data
  const educationData = [
    { qualification: 'ITIL v3 Foundation', year: '2019', type: 'certification' },
    { qualification: 'Functional Skills English', year: '2019', type: 'certification' },
    { qualification: 'MTA: Software Development Fundamentals', year: '2019', type: 'mta' },
    { qualification: 'MTA: Mobility & Devices Fundamentals', year: '2019', type: 'mta' },
    { qualification: 'MTA: Cloud Fundamentals', year: '2019', type: 'mta' },
    { qualification: 'MTA: Networking Fundamentals', year: '2018', type: 'mta' },
    { qualification: 'WorkSkills Level 2', year: '2017-2018', type: 'certification' },
    { qualification: 'BTEC IT Level 3 (Merit, Merit)', institution: 'Liverpool City College', year: '2016-2017', type: 'qualification' },
    { qualification: 'GCSEs', institution: 'St Ambrose Barlow High School', year: '2011-2016', type: 'qualification' }
  ];

  // GCSE subjects
  const gcseSubjects = [
    'Resistant Materials: B',
    'ICT: C', 
    'Mathematics: C',
    'Religious Education: C',
    'Cambridge Nationals (ICT): Merit'
  ];

  const SkillCategory: React.FC<{ title: string; skills: string[]; icon: React.ReactNode }> = ({ title, skills, icon }) => (
    <motion.div variants={itemVariants} className="card p-6">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
          {icon}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
      </div>
      <div className="space-y-3">
        {skills.map((skill, index) => (
          <div key={index} className="flex items-center">
            <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
            <span className="text-gray-700 dark:text-gray-300">{skill}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );

  const EducationItem: React.FC<{ item: any; index: number }> = ({ item, index }) => (
    <motion.div
      variants={itemVariants}
      className={`p-4 rounded-lg border-l-4 ${
        item.type === 'mta' 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500' 
          : item.type === 'certification'
          ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
          : 'bg-gray-50 dark:bg-gray-800 border-gray-500'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 dark:text-white">{item.qualification}</h4>
          {item.institution && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{item.institution}</p>
          )}
        </div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 ml-4">
          {item.year}
        </span>
      </div>
    </motion.div>
  );

  return (
    <section id="skills" className="section-padding">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 variants={itemVariants} className="text-4xl font-display font-bold mb-4">
            Skills & Qualifications
          </motion.h2>
          <motion.div variants={itemVariants} className="w-24 h-1 bg-primary-600 mx-auto mb-8"></motion.div>
        </motion.div>

        {/* Key Skills Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="mb-16"
        >
          <motion.h3 variants={itemVariants} className="text-2xl font-display font-semibold text-center mb-8">
            Key Skills & Proficiencies
          </motion.h3>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <SkillCategory
              title="IT Support"
              skills={skillsData.itSupport}
              icon={<ComputerDesktopIcon className="w-6 h-6 text-primary-600" />}
            />
            <SkillCategory
              title="Hardware"
              skills={skillsData.hardware}
              icon={<WrenchScrewdriverIcon className="w-6 h-6 text-primary-600" />}
            />
            <SkillCategory
              title="Operating Systems"
              skills={skillsData.operatingSystems}
              icon={<CogIcon className="w-6 h-6 text-primary-600" />}
            />
            <SkillCategory
              title="Productivity Suites"
              skills={skillsData.productivity}
              icon={<DocumentTextIcon className="w-6 h-6 text-primary-600" />}
            />
            <SkillCategory
              title="Administration"
              skills={skillsData.administration}
              icon={<AcademicCapIcon className="w-6 h-6 text-primary-600" />}
            />
            <SkillCategory
              title="Web Development"
              skills={skillsData.web}
              icon={<GlobeAltIcon className="w-6 h-6 text-primary-600" />}
            />
          </motion.div>

          {/* Personal Skills */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="mt-8"
          >
            <SkillCategory
              title="Personal Skills"
              skills={skillsData.personal}
              icon={<UserIcon className="w-6 h-6 text-primary-600" />}
            />
          </motion.div>
        </motion.div>

        {/* Education & Qualifications Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="mb-16"
        >
          <motion.h3 variants={itemVariants} className="text-2xl font-display font-semibold text-center mb-8">
            Education & Qualifications
          </motion.h3>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="space-y-4"
          >
            {educationData.map((item, index) => (
              <EducationItem key={index} item={item} index={index} />
            ))}
          </motion.div>

          {/* GCSE Subjects */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="mt-8"
          >
            <motion.h4 variants={itemVariants} className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              GCSE Subjects
            </motion.h4>
            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {gcseSubjects.map((subject, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <TrophyIcon className="w-5 h-5 text-primary-600 mr-3" />
                  <span className="text-gray-700 dark:text-gray-300">{subject}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Personal Details Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="card p-8"
        >
          <motion.h3 variants={itemVariants} className="text-2xl font-display font-semibold text-center mb-8">
            Personal Details
          </motion.h3>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <motion.div variants={itemVariants} className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Driving</h4>
              <p className="text-gray-600 dark:text-gray-300">Full UK Driver's License (Cars)</p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                <GlobeAltIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Work Eligibility</h4>
              <p className="text-gray-600 dark:text-gray-300">Eligible to work in the UK</p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                <TrophyIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Relocation</h4>
              <p className="text-gray-600 dark:text-gray-300">Willing to relocate anywhere</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
